import React, { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setSelectedDocumentType } from '../../../store/company/slice';
import { CompanyWithDocuments } from '../../../store/company/types';
import DocumentCategoryUtils from './DocumentCategoryUtils';
import { Text } from '../../Typography';
import DocumentList from './DocumentList';
import styles from './CompanySection.module.scss';
import { DocumentCategory, DocumentType, FilingType } from '../../../store/document/types';
import EightKList from './EightKList';
import { closeFileExplorer } from '../../../store/app/slice';
import { openDocument } from '../../../store/document/slice';
import { trackEvent } from '../../../services/tracking';

interface CompanySectionProps {
  company: CompanyWithDocuments;
}

const CompanySection: React.FC<CompanySectionProps> = ({ company }) => {
  const dispatch = useAppDispatch();
  const [selectedDocType, setSelectedDocType] = useState<DocumentType | null>(null);
  const isMobile = useAppSelector((state) => state.app.isMobile);

  // Get document categories with non-empty documents
  const docCategories = useMemo(
    () => (company.documents ? DocumentCategoryUtils.getNonEmptyCategories(company.documents) : []),
    [company.documents],
  );

  const handleDocTypeChange = (docType: DocumentType, category: DocumentCategory) => {
    const willSelect = docType !== selectedDocType;
    trackEvent('FileExplorer DocType Clicked', {
      companyId: company.id,
      ticker: company.ticker,
      category,
      docType,
      selected: willSelect,
    });
    if (willSelect) {
      setSelectedDocType(docType);
      dispatch(setSelectedDocumentType(docType));
    } else {
      setSelectedDocType(null);
      dispatch(setSelectedDocumentType(null));
    }
  };

  const handleDocumentClick = (docId: string, category?: DocumentCategory) => {
    trackEvent('FileExplorer Document Clicked', {
      companyId: company.id,
      ticker: company.ticker,
      documentId: docId,
      category,
      docType: selectedDocType || undefined,
    });
    dispatch(openDocument({ documentId: docId }));
    if (isMobile) {
      dispatch(closeFileExplorer());
    }
  };

  const selected8K = selectedDocType === FilingType.EIGHT_K;

  return (
    <div className={styles.companySection}>
      {docCategories.map((category) => {
        const docTypes = DocumentCategoryUtils.getDocumentTypesByCategory(
          company.documents,
          category,
        );

        if (docTypes.length === 0) return null;

        return (
          <div key={`categories-${category}`} className={styles.documentCategory}>
            <Text size="base" weight="semibold" className={styles.categoryHeader}>
              {DocumentCategoryUtils.formatCategoryName(category)}
            </Text>

            <div className={styles.docTypeSelector}>
              {docTypes.map((docType) => (
                <button
                  key={docType}
                  className={`${styles.docTypeButton} ${selectedDocType === docType ? styles.selected : ''}`}
                  onClick={() => handleDocTypeChange(docType, category)}
                >
                  {DocumentCategoryUtils.formatDocumentTypeName(docType)}
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {docCategories.map((category) => {
        const docTypes = DocumentCategoryUtils.getDocumentTypesByCategory(
          company.documents,
          category,
        );

        if (docTypes.length === 0) return null;

        return (
          <div key={`documents-${category}`} className={styles.documentCategory}>
            {selectedDocType && !selected8K && (
              <DocumentList
                documents={DocumentCategoryUtils.getDocumentsByType(
                  company.documents,
                  category,
                  selectedDocType,
                )}
                onDocumentClick={(id) => handleDocumentClick(id, category)}
              />
            )}

            {category === DocumentCategory.FILING && selected8K && (
              <EightKList
                companyDocuments={company.documents}
                onDocumentClick={(id) => handleDocumentClick(id, category)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CompanySection;
